import { DateTime } from "luxon";
import { CONFIG } from "../config";
import {
	CURRENCY_EUR,
	CURRENCY_LEK,
	CURRENCY_DEN,
} from "../api/currency/types";

/**
 * Symbol for a specific currency id.
 *
 * Falls back to the deployment-wide `CONFIG.CURRENCY` whenever the id is unknown or missing, so
 * a caller that has no per-record currency keeps printing exactly what it printed before.
 */
export const currencySymbol = (currencyId?: number | null): string => {
	switch (currencyId) {
		case CURRENCY_EUR:
			return "€";
		case CURRENCY_LEK:
			return "L";
		case CURRENCY_DEN:
			return "ден";
		default:
			return CONFIG?.CURRENCY;
	}
};

/**
 * Short code for a specific currency id — `LEK`, `EUR`, `DEN`.
 *
 * This is a **label**, not a symbol: it heads a column or badges a block so the reader can tell
 * two independent balances apart at a glance. Never append it to an amount — that is
 * {@link currencySymbol}'s job.
 *
 * Keyed on the id constants for the same reason `currencySymbol` is: a code derived from an id
 * cannot be wrong, whereas a `const CURRENCY = "€"` used as *the* symbol for *any* amount is the
 * bug this codebase has already shipped three times.
 */
export const currencyCode = (currencyId?: number | null): string => {
	switch (currencyId) {
		case CURRENCY_EUR:
			return "EUR";
		case CURRENCY_LEK:
			return "LEK";
		case CURRENCY_DEN:
			return "DEN";
		default:
			return CONFIG?.CURRENCY;
	}
};

/**
 * Spelled-out currency name — `EURO`, `LEK`, `DEN`.
 *
 * The label an **exported spreadsheet** prints in the currency column that follows every money
 * column. Distinct from {@link currencyCode} on purpose: a grid header has a few characters to
 * spend and `EUR` reads fine there, while a sheet column that exists solely to say what the number
 * next to it is should say it in full — and `EURO` is what `currency.Description` holds in every
 * database, so the sheet and the currency-management screen agree.
 *
 * Falls back to an empty string rather than to a symbol: this labels a column of its own, and a
 * `€` sitting alone in a cell headed "Valuta" is the confusion the split column removes.
 */
export const currencyName = (currencyId?: number | null): string => {
	switch (currencyId) {
		case CURRENCY_EUR:
			return "EURO";
		case CURRENCY_LEK:
			return "LEK";
		case CURRENCY_DEN:
			return "DEN";
		default:
			return "";
	}
};

/**
 * Format an amount in the currency it is actually held in.
 *
 * Use this wherever a record carries its own `currencyId` — order prices, balances, settlement
 * values. Where there is no per-record currency, keep using {@link priceFormatter}.
 */
export const formatMoney = (data: any, currencyId?: number | null) => {
	if (data === null || data === undefined || data?.toString().trim() === "") {
		return "-";
	}
	return Number(data).toFixed(2) + currencySymbol(currencyId);
};

/**
 * AG-Grid `valueFormatter` that reads the currency from a sibling field on the same row.
 *
 * ```ts
 * { field: "orderPrice", valueFormatter: priceFormatterByCurrency() }
 * { field: "orderPrice", valueFormatter: priceFormatterByCurrency("deliveredCurrencyId") }
 * ```
 *
 * A row without that field falls through to the deployment symbol, so this is safe to adopt one
 * grid at a time.
 */
export const priceFormatterByCurrency =
	(currencyField: string = "currencyId") =>
	(params: any) =>
		formatMoney(params?.value, params?.data?.[currencyField]);

/** {@link thousandsFormatter} with a per-record currency. */
export const thousandsFormatterByCurrency =
	(currencyField: string = "currencyId") =>
	(params: any) => {
		const data = params?.value;
		if (
			data === null ||
			data === undefined ||
			data?.toString().trim() === ""
		) {
			return "-";
		}
		return (
			new Intl.NumberFormat("en-US", {
				maximumFractionDigits: 2,
				minimumFractionDigits: 2,
			}).format(data) + currencySymbol(params?.data?.[currencyField])
		);
	};

/**
 * AG-Grid `valueFormatter` for a figure that can exist in **both** currencies on the same row —
 * a settlement value, above all.
 *
 * @deprecated Superseded by `dualMoneyColumns()` in `utils/moneyColumns.ts`, which gives each
 * currency its own column instead of crowding both into one cell. Cramming them together made LEK
 * read as the amount and EUR as an afterthought, which is the opposite of what they are. Kept only
 * for the call sites not yet migrated.
 *
 * A single settlement is partitioned by the currency each of its orders was registered in and paid
 * out as two separate cash-register transactions, so `sumValue` and `sumValueEur` are independent
 * amounts. This renders them as `45,000.00 L · 320.00 €` and **never adds them**.
 *
 * The EUR half is omitted when it is zero, null or absent — which is always the case outside
 * PostmanAL, so this is safe to adopt on every grid without a feature gate. Needs no hook, which
 * is why it lives here rather than in a component.
 *
 * ```ts
 * { field: "sumValue", valueFormatter: dualMoneyFormatter() }
 * { field: "sumValue", valueFormatter: dualMoneyFormatter("sumValueEur", "currencyId") }
 * ```
 */
export const dualMoneyFormatter =
	(eurField: string = "sumValueEur", currencyField: string = "currencyId") =>
	(params: any) => {
		const local = params?.value;
		if (
			local === null ||
			local === undefined ||
			local?.toString().trim() === ""
		) {
			return "-";
		}

		const localText =
			Number(local).toFixed(2) +
			currencySymbol(params?.data?.[currencyField]);

		const eur = params?.data?.[eurField];
		if (eur === null || eur === undefined || Number(eur) === 0) {
			return localText;
		}

		return `${localText} · ${Number(eur).toFixed(2)}${currencySymbol(
			CURRENCY_EUR
		)}`;
	};

export const codeFormatter = (data: any) => {
	if (data === null || data === undefined || data?.toString().trim() === "") {
		return "-";
	} else {
		return "#" + data;
	}
};

export const priceFormatter = (data: any) => {
	if (data === null || data === undefined || data?.toString().trim() === "") {
		return "-";
	} else {
		return Number(data).toFixed(2) + CONFIG?.CURRENCY;
	}
};

export const valueDateFormatter = (data: string) => {
	if (data === null) {
		return "-";
	} else {
		return new Date(data).toLocaleString(["en-GB"], {
			year: "numeric",
			month: "numeric",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	}
};

export const getStartOfTheMonth = () => {
	const now = DateTime.now();

	const date = now.startOf("month").setLocale("en-gb").toLocaleString({
		day: "numeric",
		month: "numeric",
		year: "numeric",
	});
	return date;
};
export const getEndOfTheMonth = () => {
	const now = DateTime.now();

	const date = now.endOf("month").setLocale("en-gb").toLocaleString({
		day: "numeric",
		month: "numeric",
		year: "numeric",
	});
	return date;
};

export const thousandsFormatter = (data: any) => {
	if (data === null || data === undefined || data?.toString().trim() === "") {
		return "-";
	}
	return (
		new Intl.NumberFormat("en-US", {
			maximumFractionDigits: 2,
			minimumFractionDigits: 2,
		}).format(data) + CONFIG?.CURRENCY
	);
};
